
import React, { useState } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCardIcon, BanknoteIcon, CheckIcon, AlertCircleIcon, FileTextIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

interface BenposVerificationProps {
  data: any[];
  tdsWaivers: Record<string, boolean>;
  onComplete: () => void;
}

export const BenposVerification: React.FC<BenposVerificationProps> = ({ 
  data, 
  tdsWaivers,
  onComplete
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [verifiedAccounts, setVerifiedAccounts] = useState<Record<string, boolean>>({});
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Start penny drop verification process
  const startVerification = () => {
    setIsVerifying(true);
    setVerificationProgress(0);
    
    // Simulate verification process with progress updates
    const totalAccounts = data.length;
    let verifiedCount = 0;
    const tempVerified: Record<string, boolean> = {};
    
    // Reset verification status
    setVerifiedAccounts({});
    
    const interval = setInterval(() => {
      if (verifiedCount < totalAccounts) {
        // Simulate random success/failure (90% success rate)
        const isSuccess = Math.random() > 0.1;
        tempVerified[data[verifiedCount].folio] = isSuccess;
        
        verifiedCount++;
        const progress = Math.floor((verifiedCount / totalAccounts) * 100);
        setVerificationProgress(progress);
        setVerifiedAccounts({...tempVerified});
        
        if (verifiedCount === totalAccounts) {
          clearInterval(interval);
          setIsVerifying(false);
          toast.success("Account verification completed");
        }
      }
    }, 800); // Process one account every 800ms for demo
  };
  
  // Process payment after confirmation
  const processPayment = () => {
    setIsProcessingPayment(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessingPayment(false);
      setShowPaymentDialog(false);
      toast.success("Payments processed successfully");
      onComplete(); // Move to the next step
    }, 2000);
  };
  
  // Calculate verification stats
  const totalAccounts = data.length;
  const verifiedCount = Object.values(verifiedAccounts).filter(Boolean).length;
  const failedCount = Object.values(verifiedAccounts).filter(v => v === false).length;
  
  // Calculate total payout amount
  const totalPayoutAmount = data.reduce((total, item) => {
    const faceValue = item.faceValue;
    const interestAmount = (8.75 / 100) * faceValue; // Using default 8.75% interest
    const hasTdsWaiver = tdsWaivers[item.folio];
    const tdsAmount = hasTdsWaiver ? 0 : (10 / 100) * interestAmount; // TDS applicable if no waiver
    const netPayout = interestAmount - tdsAmount;
    return total + netPayout;
  }, 0);

  return (
    <div className="space-y-6">
      {/* Verification Status */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Account Verification Status</h3>
            
            {isVerifying ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Verifying accounts...</span>
                  <span>{verificationProgress}% complete</span>
                </div>
                <Progress value={verificationProgress} className="h-2" />
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-col md:flex-row gap-6">
                  <div>
                    <div className="text-sm text-muted-foreground">Total Accounts</div>
                    <div className="text-2xl font-bold">{totalAccounts}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Verified</div>
                    <div className="text-2xl font-bold text-green-600">{verifiedCount}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Failed</div>
                    <div className="text-2xl font-bold text-red-600">{failedCount}</div>
                  </div>
                </div>
                
                <div>
                  <Button 
                    onClick={startVerification} 
                    disabled={isVerifying}
                  >
                    <BanknoteIcon className="h-4 w-4 mr-2" />
                    {Object.keys(verifiedAccounts).length > 0 ? "Re-run Verification" : "Start Verification"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Verification Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio Number</TableHead>
              <TableHead>Beneficiary Name</TableHead>
              <TableHead>Bank Account</TableHead>
              <TableHead>IFSC Code</TableHead>
              <TableHead>TDS Status</TableHead>
              <TableHead className="text-right">Payout Amount (₹)</TableHead>
              <TableHead>Verification Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => {
              // Calculate payout amount with TDS waiver consideration
              const faceValue = item.faceValue;
              const interestAmount = (8.75 / 100) * faceValue; // Using default 8.75% interest
              const hasTdsWaiver = tdsWaivers[item.folio];
              const tdsAmount = hasTdsWaiver ? 0 : (10 / 100) * interestAmount; // TDS applicable if no waiver
              const netPayout = interestAmount - tdsAmount;
              
              // Get verification status
              const isVerified = verifiedAccounts[item.folio];
              let verificationStatus = "Pending";
              let badgeVariant: "outline" | "secondary" | "destructive" = "secondary";
              
              if (isVerified === true) {
                verificationStatus = "Verified";
                badgeVariant = "outline";
              } else if (isVerified === false) {
                verificationStatus = "Failed";
                badgeVariant = "destructive";
              }
              
              return (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.folio}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.bankAccount}</TableCell>
                  <TableCell>{item.ifscCode}</TableCell>
                  <TableCell>
                    {hasTdsWaiver ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        Waiver Applied
                      </Badge>
                    ) : (
                      <Badge variant="outline">Standard TDS (10%)</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    <Badge variant={badgeVariant}>
                      {verificationStatus}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline">
          <FileTextIcon className="h-4 w-4 mr-2" />
          Export Verification Report
        </Button>
        <Button 
          onClick={() => setShowPaymentDialog(true)}
          disabled={isVerifying || verifiedCount === 0 || verifiedCount !== totalAccounts}
        >
          <CreditCardIcon className="h-4 w-4 mr-2" />
          Process Payment
        </Button>
      </div>
      
      {/* Payment Confirmation Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Confirmation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border rounded-md p-4 bg-muted/30">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Beneficiaries:</span>
                  <span className="font-medium">{verifiedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payment Date:</span>
                  <span className="font-medium">{new Date().toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Amount:</span>
                  <span className="font-medium">₹{totalPayoutAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payment From:</span>
                  <span className="font-medium">Primary Treasury Account</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <AlertCircleIcon className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
              <p className="text-sm">
                This action will initiate payment to all verified beneficiaries. The payment process 
                cannot be reversed once initiated. Please confirm to proceed.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} disabled={isProcessingPayment}>
              Cancel
            </Button>
            <Button onClick={processPayment} disabled={isProcessingPayment}>
              {isProcessingPayment ? (
                <>Processing...</>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Confirm & Process Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
